import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { UserInvitationClientService } from '../../../../core/api/api/userInvitation-client.service';
import { firstValueFrom } from 'rxjs';
import { FormElementComponent } from '../../../../shared/components/form-inputs/components/form-element/form-element.component';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';
import { LegalLinksContainerComponent } from '../../../../shared/components/legal-links-container/legal-links-container.component';
import { TRANSLOCO_SCOPE, TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { TogglePasswordDirective } from '../../../../shared/directives/toggle-password.directive';
import { PasswordStrengthComponent } from '../../../../shared/components/password-strength/password-strength.component';
import { FeedbackService } from '../../../../services/feedback.service';
import { AuthService } from '../../../../services/auth.service';
import { UserService } from '../../../../services/user.service';
import { PasswordPolicyDto } from '../../../api/model/passwordPolicyDto';
import { UserInvitationResponseDto } from '../../../api';
import { UserInvitationStatus } from '../../../api';
import { PasswordPolicyService } from '../../../auth/services/password-policy.service';
import { PasswordRequirementsPipe } from '../../../../shared/pipes/password-requirements.pipe';
import {
  passwordMatchValidator,
  passwordStrengthValidator,
  passwordStrengthValidatorWithPolicy,
  isPasswordValid
} from '../../../../shared/validators/password.validators';

@Component({
  selector: 'app-accept-invitation',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    FormElementComponent,
    LoaderComponent,
    LegalLinksContainerComponent,
    TranslocoDirective,
    TogglePasswordDirective,
    PasswordStrengthComponent,
    PasswordRequirementsPipe
  ],
  templateUrl: './accept-invitation.component.html',
  styleUrl: './accept-invitation.component.css',
  standalone: true,
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'auth' }]
})
export class AcceptInvitationComponent implements OnInit {
  private readonly _userInvitationService = inject(UserInvitationClientService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _translocoService = inject(TranslocoService);
  private readonly _feedbackService = inject(FeedbackService);
  private readonly _authService = inject(AuthService);
  private readonly _userService = inject(UserService);
  private readonly _passwordPolicyService = inject(PasswordPolicyService);

  public invitationToken = '';
  public email = '';
  public isLoading = false;
  public invitationData: UserInvitationResponseDto | null = null;
  public invitationState: 'valid' | 'expired' | 'alreadyUsed' | 'withdrawn' | 'inactive' | 'notFound' = 'valid';
  public isOwnerInvitation = false;
  public passwordPolicy: PasswordPolicyDto | null = null;

  public acceptInvitationForm: FormGroup;

  constructor() {
    this.acceptInvitationForm = new FormGroup(
      {
        password: new FormControl('', [Validators.required, passwordStrengthValidator()]),
        confirmPassword: new FormControl('', [Validators.required])
      },
      { validators: passwordMatchValidator() }
    );
  }

  async ngOnInit(): Promise<void> {
    this.invitationToken = this._route.snapshot.paramMap.get('token') || '';

    if (!this.invitationToken) {
      this._router.navigate(['/auth/login']);
      return;
    }

    const policy = await this._passwordPolicyService.ensurePolicyLoaded();
    this.passwordPolicy = policy;
    const passwordControl = this.acceptInvitationForm.get('password')!;
    passwordControl.setValidators([Validators.required, passwordStrengthValidatorWithPolicy(policy)]);
    passwordControl.updateValueAndValidity();

    await this.loadInvitationDetails();
  }

  private async loadInvitationDetails(): Promise<void> {
    this.isLoading = true;
    try {
      const invitation = await firstValueFrom(this._userInvitationService.getInvitationByToken(this.invitationToken));
      if (invitation.status !== 'Pending') {
        this._router.navigate(['/auth/invitation/invalid']);
        return;
      }
      this.invitationData = invitation;
      this.email = invitation.email || '';
      this.isOwnerInvitation = invitation.isOwnerInvitation || false;

      // Check invitation state
      this.invitationState = this.determineInvitationState(invitation);

      // If invitation is not valid, show appropriate message
      if (this.invitationState !== 'valid') {
        this.showInvitationStateMessage();
      }
    } catch (error) {
      console.error('Failed to load invitation details:', error);
      this.invitationState = 'notFound';
      this.showInvitationStateMessage();
    } finally {
      this.isLoading = false;
    }
  }

  private determineInvitationState(
    invitation: UserInvitationResponseDto
  ): 'valid' | 'expired' | 'alreadyUsed' | 'withdrawn' | 'inactive' | 'notFound' {
    // Check if invitation has expired
    if (invitation.expiresAt) {
      const expirationDate = new Date(invitation.expiresAt);
      const now = new Date();
      if (now > expirationDate) {
        return 'expired';
      }
    }

    // Check invitation status
    switch (invitation.status) {
      case UserInvitationStatus.Accepted:
        return 'alreadyUsed';
      case UserInvitationStatus.Withdrawn:
        return 'withdrawn';
      case UserInvitationStatus.Inactive:
        return 'inactive';
      case UserInvitationStatus.Pending:
        return 'valid';
      default:
        return 'notFound';
    }
  }

  private showInvitationStateMessage(): void {
    let messageKey = '';

    /** t(auth.invitation.invalidToken) */
    /** t(auth.invitation.expired) */
    /** t(auth.invitation.alreadyUsed) */
    /** t(auth.invitation.withdrawn) */
    /** t(auth.invitation.inactive) */
    /** t(auth.invitation.notFound) */
    switch (this.invitationState) {
      case 'expired':
        messageKey = 'invitation.expired';
        break;
      case 'alreadyUsed':
        messageKey = 'invitation.alreadyUsed';
        break;
      case 'withdrawn':
        messageKey = 'invitation.withdrawn';
        break;
      case 'inactive':
        messageKey = 'invitation.inactive';
        break;
      case 'notFound':
        messageKey = 'invitation.notFound';
        break;
    }

    if (messageKey) {
      this._feedbackService.error(this._translocoService.translate(messageKey, {}, 'auth'));
    }
  }

  public get isPasswordValid(): boolean {
    const password = this.acceptInvitationForm.get('password')?.value || '';
    const confirmPassword = this.acceptInvitationForm.get('confirmPassword')?.value || '';
    return isPasswordValid(password, confirmPassword, this.passwordPolicy);
  }

  public async onSubmit(): Promise<void> {
    if (this.acceptInvitationForm.invalid || !this.invitationToken || this.invitationState !== 'valid') {
      return;
    }

    const formValues = this.acceptInvitationForm.value;
    this.isLoading = true;

    try {
      await this._authService.acceptInvitation(this.invitationToken, formValues.password);

      // Load user data after successful invitation acceptance
      await this._userService.loadCurrentUser();

      this._feedbackService.success(this._translocoService.translate('invitation.acceptSuccess', {}, 'auth'));

      // Redirect to dashboard after successful acceptance
      this._router.navigate(['/main/dashboard']);
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      this._feedbackService.error(this._translocoService.translate('invitation.acceptError', {}, 'auth'));
    } finally {
      this.isLoading = false;
    }
  }

  public onConfirmPasswordFocus(): void {
    const confirmPasswordControl = this.acceptInvitationForm.get('confirmPassword');
    if (confirmPasswordControl && !confirmPasswordControl.touched) {
      confirmPasswordControl.markAsTouched();
      // Trigger validation by updating the form
      this.acceptInvitationForm.updateValueAndValidity();
    }
  }
}
